"use client";
import { useState } from "react";
import Checkbox from "../../common/form/checkbox";
import { RadioBool } from "../../common/form/radioBool";
import DatePicker from "../../common/form/datePicker";
import { DropDownField } from "../../common/form/dropdownField";
import { TextField } from "../../common/form/textField";
import { FormRow } from "../../common/form/layouts/formRow";
import FormSubSection from "../../common/form/layouts/formSubSection";
import Upload from "../../common/form/upload";
import TextLink from "../../common/textLink";
import TextArea from "../../common/form/textArea";
import Recaptcha from "../../common/form/recaptcha";
import CustomButton from "../../common/button";
import LoadingWidget from "../../common/loadingWidget";
import ErrorMessageStatus from "../../common/system-messages/errorMessage";
import SuccessMessageStatus from "../../common/system-messages/successMessage";
import { validateEmail, validatePhone } from "~/app/utils/helper";
import { PrivacyPolicy } from "~/content/navLinks";
import { useReCaptcha } from "next-recaptcha-v3";

type ReferralFormType = {
  isParticipant?: boolean;
  consent?: boolean;
  servicesRequired?: string[];
  participant?: {
    name?: string;
    preferredName?: string;
    dob?: string;
    gender?: string;
    pronouns?: string;
  };
  referrer?: {
    name?: string;
    phone?: string;
    email?: string;
  };
  reason?: string;
  primaryDisability?: string;
  NDIS?: {
    number?: string;
    funded?: string;
    startDate?: string;
    endDate?: string;
    upload?: Blob;
  };
  disclaimer?: boolean;
  isVerified?: boolean;
};
type ReferralFormErrorsType = {
  isParticipant?: string;
  consent?: string;
  servicesRequired?: string;
  participant?: {
    name?: string;
    preferredName?: string;
    dob?: string;
    gender?: string;
    pronouns?: string;
  };
  referrer?: {
    name?: string;
    phone?: string;
    email?: string;
  };
  reason?: string;
  primaryDisability?: string;
  NDIS?: {
    number?: string;
    funded?: string;
    startDate?: string;
    endDate?: string;
    upload?: string;
  };
  disclaimer?: string;
  isVerified?: string;
};

enum FormStatus {
  IDLE,
  PENDING,
  SUCCESS,
  ERROR,
}

export default function ReferralForm() {
  const [formData, setFormData] = useState<ReferralFormType>({});
  const [fieldErrors, setFieldErrors] = useState<
    Partial<ReferralFormErrorsType>
  >({});
  const [generalError, setGeneralError] = useState<string>("");
  const [formStatus, setFormStatus] = useState<FormStatus>(FormStatus.IDLE);

  const addRemoveService = (service: string, action: boolean) => {
    if (action) {
      if (formData.servicesRequired) {
        setFormData({
          ...formData,
          servicesRequired: [...formData.servicesRequired, service],
        });
      } else {
        setFormData({ ...formData, servicesRequired: [service] });
      }
    } else {
      if (formData.servicesRequired) {
        setFormData({
          ...formData,
          servicesRequired: formData.servicesRequired.filter(
            (val) => val !== service,
          ),
        });
      }
    }
  };

  const validateFormData = (
    data: ReferralFormType,
  ): Partial<ReferralFormErrorsType> | null => {
    const errors: Partial<ReferralFormErrorsType> = {};

    if (data.isParticipant === undefined)
      errors.isParticipant = "This field is required.";
    if (data.isParticipant === false && data.consent === undefined)
      errors.consent = "Consent is required.";
    if (!data.isParticipant) {
      const emailError: string | undefined = validateEmail(
        data.referrer?.email,
      );
      const phoneError: string | undefined = validatePhone(
        data.referrer?.phone,
      );

      if (data.referrer?.name === undefined)
        errors.referrer = {
          ...errors.referrer,
          name: "Referrer name is required.",
        };
      if (phoneError)
        errors.referrer = { ...errors.referrer, phone: phoneError };
      if (emailError)
        errors.referrer = { ...errors.referrer, email: emailError };
    }
    if (!data.servicesRequired || data.servicesRequired.length === 0)
      errors.servicesRequired = "At least one service is required.";
    if (!data.participant?.name)
      errors.participant = {
        ...errors.participant,
        name: "Participant name is required.",
      };
    if (!data.participant?.dob)
      errors.participant = {
        ...errors.participant,
        dob: "Date of birth is required.",
      };
    if (data.participant?.gender === "")
      errors.participant = {
        ...errors.participant,
        gender: "Gender is Required",
      };
    if (data.participant?.pronouns === "")
      errors.participant = {
        ...errors.participant,
        pronouns: "Pronouns is Required",
      };
    if (!data.reason) errors.reason = "Reason for referral is required.";
    if (!data.primaryDisability)
      errors.primaryDisability = "Primary disability is required.";
    // if (!data.NDIS?.number)
    //   errors.NDIS = { ...errors.NDIS, number: "NDIS number is required." };
    // if (!data.NDIS?.funded)
    //   errors.NDIS = {
    //     ...errors.NDIS,
    //     funded: "NDIS funding information is required.",
    //   };
    // if (!data.NDIS?.startDate)
    //   errors.NDIS = {
    //     ...errors.NDIS,
    //     startDate: "NDIS start date is required.",
    //   };
    // if (!data.NDIS?.endDate)
    //   errors.NDIS = { ...errors.NDIS, endDate: "NDIS end date is required." };
    // if (!data.NDIS?.upload)
    //   errors.NDIS = { ...errors.NDIS, upload: "Please upload NDIS Plan." };
    // if (!data.isVerified)
    //   errors.isVerified = "Recaptcha verification is required.";
    if (!data.disclaimer)
      errors.disclaimer = "You must agree to the disclaimer.";

    return Object.keys(errors).length > 0 ? errors : null;
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // v3 RECAPTCHA useReCaptcha hook - import 'executeRecaptcha' using 'useReCaptcha' hook
  const { executeRecaptcha } = useReCaptcha();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    // Perform validation
    const validationErrors = validateFormData(formData);
    if (validationErrors) {
      setFieldErrors(validationErrors);
      setGeneralError("Please fill out all required fields correctly.");
      scrollToTop();
      return;
    }

    // v3 RECAPTCHA - check the availability of execution before retrieving token
    if (!executeRecaptcha) {
      setGeneralError("Not available to execute recaptcha");
      setFormStatus(FormStatus.IDLE);
      return;
    }
    const token = await executeRecaptcha("referral_form_submit");
    // verify recaptcha before sending it to the server
    if (!token) {
      setGeneralError("reCAPTCHA verification failed, token is not available!");
      setFormStatus(FormStatus.IDLE);
      return;
    }

    // Submit form data
    setGeneralError("");
    setFieldErrors({});
    setFormStatus(FormStatus.PENDING);

    // check if form data is passing
    // console.log("form data: ", buildSendGridMessage(formData));

    // verify the reCAPTCHA token on the server
    try {
      const verifyRepsonse = await fetch("/api/verify-recaptcha", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      const verifyResult = (await verifyRepsonse.json()) as {
        success?: boolean;
        score?: number;
      };
      console.log("Verify Result: ", verifyResult); // testing

      if (!verifyResult.success) {
        setGeneralError(`reCAPTCHA verification failed, Please try again.`);
        console.log("Failure with score: ", verifyResult.score);
        setFormStatus(FormStatus.IDLE);
        return;
      } else {
        console.log("Success with score: ", verifyResult.score);
      }
    } catch (error) {
      console.error("Error during reCAPTCHA verfication");
      setFormStatus(FormStatus.ERROR);
    }

    // SendGrid API call
    const message = await buildSendGridMessage(formData);
    fetch("/api/sendgrid", {
      method: "POST",
      body: JSON.stringify({ msg: message }),
      // headers: {
      //     "Content-Type": "application/json",
      // },
    })
      .then((res) => {
        if (res.ok) {
          setFormStatus(FormStatus.SUCCESS);
        } else {
          setFormStatus(FormStatus.ERROR);
        }
      })
      .catch(() => {
        setFormStatus(FormStatus.ERROR);
      });

    scrollToTop();
  };

  if (formStatus === FormStatus.PENDING)
    return (
      <div className="flex w-full flex-row justify-center">
        <LoadingWidget />
      </div>
    );
  if (formStatus === FormStatus.ERROR)
    return (
      <div className="flex w-full flex-row justify-center">
        <ErrorMessageStatus
          errorMessage={
            "Unfortunately we are experiencing system errors right now and unable to process your request."
          }
        />
      </div>
    );
  if (formStatus === FormStatus.SUCCESS)
    return (
      <div className="flex w-full flex-row justify-center">
        <SuccessMessageStatus
          title="Enquiry submitted"
          message="Thank you for your enquiry. We will be in touch shortly."
        />
      </div>
    );
  return (
    <>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        {generalError && <p className="text-red-500">{generalError}</p>}
        <div>
          {fieldErrors.isParticipant && (
            <p className="text-red-500">{fieldErrors.isParticipant}</p>
          )}
          <RadioBool
            text="Are you submitting this referral on behalf of a participant?"
            options={{ trueText: "Myself", falseText: "Someone else" }}
            setOptions={(val) =>
              setFormData({ ...formData, isParticipant: val })
            }
            id="participantReferral"
            titleClass="text-desk-resp-xl"
            labelClass="text-resp-lg"
          />
        </div>
        {formData.isParticipant === false && (
          <div>
            {formData.consent === false && (
              <p className="text-red-500">
                {
                  "You will not be able to proceed until you have obtained consent from the participant."
                }
              </p>
            )}
            <RadioBool
              text="Do you have consent from the person that you are referring or their representative to share the information in this form?"
              options={{ trueText: "Yes", falseText: "No" }}
              setOptions={(val) => setFormData({ ...formData, consent: val })}
              id="consent"
            />
          </div>
        )}
        {formData.isParticipant === false && (
          <FormSubSection title="Referrer Details">
            <div className="flex w-full flex-col">
              {fieldErrors.referrer?.name && (
                <p className="text-red-500">{fieldErrors.referrer.name}</p>
              )}
            </div>
            <FormRow>
              <TextField
                label="Referrer Name"
                value={formData.referrer?.name || ""}
                setValue={(value) =>
                  setFormData({
                    ...formData,
                    referrer: { ...formData.referrer, name: value },
                  })
                }
              />
            </FormRow>
            <div className="flex w-full flex-col">
              {fieldErrors.referrer?.phone && (
                <p className="text-red-500">{fieldErrors.referrer.phone}</p>
              )}
              {fieldErrors.referrer?.email && (
                <p className="text-red-500">{fieldErrors.referrer.email}</p>
              )}
            </div>
            <FormRow>
              <TextField
                label="Phone"
                value={formData.referrer?.phone || ""}
                setValue={(value) =>
                  setFormData({
                    ...formData,
                    referrer: { ...formData.referrer, phone: value },
                  })
                }
              />
              <TextField
                label="Email"
                value={formData.referrer?.email || ""}
                setValue={(value) =>
                  setFormData({
                    ...formData,
                    referrer: { ...formData.referrer, email: value },
                  })
                }
              />
            </FormRow>
          </FormSubSection>
        )}
        {(formData.isParticipant === true ||
          formData.consent !== undefined) && (
          <>
            <div className="flex flex-col gap-2">
              {fieldErrors.servicesRequired && (
                <p className="text-red-500">{fieldErrors.servicesRequired}</p>
              )}
              <FormSubSection title="Services Required">
                <div className="grid grid-cols-1 gap-x-20 xs:grid-cols-2">
                  <Checkbox
                    text="Assistance with Daily Living"
                    id="dailyLiving"
                    setValue={(val) =>
                      addRemoveService("Assistance with Daily Living", val)
                    }
                  />
                  <Checkbox
                    text="Life Skills"
                    id="lifeSkills"
                    setValue={(val) => addRemoveService("Life Skills", val)}
                  />
                  <Checkbox
                    text="Social and Community participation"
                    id="socialCommunity"
                    setValue={(val) =>
                      addRemoveService(
                        "Social and Community participation",
                        val,
                      )
                    }
                  />
                  <Checkbox
                    text="Supported Independent Living (SIL)"
                    id="SIL"
                    setValue={(val) =>
                      addRemoveService(
                        "Supported Independent Living (SIL)",
                        val,
                      )
                    }
                  />
                  <Checkbox
                    text="Short Term Accommodation (STA)"
                    id="STA"
                    setValue={(val) =>
                      addRemoveService("Short Term Accommodation (STA)", val)
                    }
                  />
                  <Checkbox
                    text="Support Coordination"
                    id="supportCoordination"
                    setValue={(val) =>
                      addRemoveService("Support Coordination", val)
                    }
                  />
                  <Checkbox
                    text="Occupational Therapy"
                    id="occupationalTherapy"
                    setValue={(val) =>
                      addRemoveService("Occupational Therapy", val)
                    }
                  />
                </div>
              </FormSubSection>
            </div>
            <FormSubSection title="Participant Details">
              <div className="flex w-full flex-col">
                {fieldErrors.participant?.name && (
                  <p className="text-red-500">{fieldErrors.participant.name}</p>
                )}
              </div>
              <FormRow>
                <TextField
                  label="Participant Name"
                  value={formData.participant?.name || ""}
                  setValue={(value) =>
                    setFormData({
                      ...formData,
                      participant: { ...formData.participant, name: value },
                    })
                  }
                />
                <TextField
                  label="Preferred Name"
                  value={formData.participant?.preferredName || ""}
                  setValue={(value) =>
                    setFormData({
                      ...formData,
                      participant: {
                        ...formData.participant,
                        preferredName: value,
                      },
                    })
                  }
                />
              </FormRow>
              <div className="flex w-full flex-col">
                {fieldErrors.participant?.dob && (
                  <p className="text-red-500">{fieldErrors.participant.dob}</p>
                )}
              </div>
              <FormRow>
                <DatePicker
                  label="Date of Birth"
                  value={formData.participant?.dob || ""}
                  onChange={(val) =>
                    setFormData({
                      ...formData,
                      participant: { ...formData.participant, dob: val },
                    })
                  }
                />
              </FormRow>
              <div className="flex w-full flex-col">
                {fieldErrors.participant?.gender && (
                  <p className="text-red-500">
                    {fieldErrors.participant.gender}
                  </p>
                )}
                {fieldErrors.participant?.pronouns && (
                  <p className="text-red-500">
                    {fieldErrors.participant.pronouns}
                  </p>
                )}
              </div>
              <FormRow>
                <DropDownField
                  label="Gender"
                  value={formData.participant?.gender || ""}
                  setValue={(value) =>
                    setFormData({
                      ...formData,
                      participant: { ...formData.participant, gender: value },
                    })
                  }
                  options={["Male", "Female", "Other"]}
                />
                <DropDownField
                  label="Pronouns"
                  value={formData.participant?.pronouns || ""}
                  setValue={(value) =>
                    setFormData({
                      ...formData,
                      participant: { ...formData.participant, pronouns: value },
                    })
                  }
                  options={["He/Him", "She/Her", "They/Them", "Other"]}
                />
              </FormRow>
            </FormSubSection>
            <FormSubSection title="Referral Details">
              <div className="flex w-full flex-col">
                {fieldErrors.reason && (
                  <p className="text-red-500">{fieldErrors.reason}</p>
                )}
              </div>
              <TextArea
                label="Reason"
                value={formData.reason || ""}
                onChange={(value) =>
                  setFormData({ ...formData, reason: value })
                }
              />
              <div className="flex w-full flex-col">
                {fieldErrors.primaryDisability && (
                  <p className="text-red-500">
                    {fieldErrors.primaryDisability}
                  </p>
                )}
              </div>
              <TextArea
                label="Primary Disability"
                value={formData.primaryDisability || ""}
                onChange={(value) =>
                  setFormData({ ...formData, primaryDisability: value })
                }
              />
            </FormSubSection>
            <FormSubSection title="NDIS Details">
              <div className="flex w-full flex-col">
                {fieldErrors.NDIS?.number && (
                  <p className="text-red-500">{fieldErrors.NDIS.number}</p>
                )}
                {fieldErrors.NDIS?.funded && (
                  <p className="text-red-500">{fieldErrors.NDIS.funded}</p>
                )}
              </div>
              <FormRow>
                <TextField
                  label="NDIS participant number"
                  value={formData.NDIS?.number || ""}
                  setValue={(value) =>
                    setFormData({
                      ...formData,
                      NDIS: { ...formData.NDIS, number: value },
                    })
                  }
                />
                <DropDownField
                  label="How is NDIS Plan Funded"
                  value={formData.NDIS?.funded || ""}
                  setValue={(value) =>
                    setFormData({
                      ...formData,
                      NDIS: { ...formData.NDIS, funded: value },
                    })
                  }
                  options={["NDIA Managed", "Plan Managed", "Self Managed"]}
                />
              </FormRow>
              <div className="flex w-full flex-col">
                {fieldErrors.NDIS?.startDate && (
                  <p className="text-red-500">{fieldErrors.NDIS.startDate}</p>
                )}
                {fieldErrors.NDIS?.endDate && (
                  <p className="text-red-500">{fieldErrors.NDIS.endDate}</p>
                )}
              </div>
              <FormRow>
                <DatePicker
                  label="NDIS start date"
                  value={formData.NDIS?.startDate || ""}
                  onChange={(val) =>
                    setFormData({
                      ...formData,
                      NDIS: { ...formData.NDIS, startDate: val },
                    })
                  }
                />
                <DatePicker
                  label="NDIS end date"
                  value={formData.NDIS?.endDate || ""}
                  onChange={(val) =>
                    setFormData({
                      ...formData,
                      NDIS: { ...formData.NDIS, endDate: val },
                    })
                  }
                />
              </FormRow>
              <div className="flex w-full flex-col">
                {fieldErrors.NDIS?.upload && (
                  <p className="text-red-500">{fieldErrors.NDIS.upload}</p>
                )}
              </div>
              <Upload
                onFileUpload={(val) =>
                  setFormData({
                    ...formData,
                    NDIS: { ...formData.NDIS, upload: val },
                  })
                }
                title="Please upload NDIS Plan."
              />
            </FormSubSection>
            <div className="flex flex-col items-center gap-2">
              <strong>
                <h2 className="">Disclaimer</h2>
              </strong>
              {fieldErrors.disclaimer && (
                <p className="text-red-500">{fieldErrors.disclaimer}</p>
              )}
              <ul className="list-inside list-disc pl-5">
                <li>
                  I agree that all the submitted referral details are accurate.
                </li>
                <li>
                  I agree with Incluso Services{" "}
                  <TextLink
                    newTab
                    text={PrivacyPolicy.name}
                    href={PrivacyPolicy.href}
                  />{" "}
                  upon submitting details.
                </li>
              </ul>
              <Checkbox
                text="I agree"
                id="agree"
                setValue={(val) =>
                  setFormData({ ...formData, disclaimer: val })
                }
              />
            </div>
            {/* v2 recaptcha key */}
            {/* <div className="flex w-full flex-row justify-center">
              <Recaptcha
                setIsverified={(val) =>
                  setFormData({ ...formData, isVerified: val })
                }
              />
            </div> */}
            <CustomButton
              type="submit"
              //   size="lg-flat"
              className="mx-auto mt-8 w-full min-w-[15rem]"
            >
              Submit
            </CustomButton>
          </>
        )}
      </form>
    </>
  );
}

async function buildSendGridMessage(formData: ReferralFormType): Promise<any> {
  const attachment = await blobToBase64(formData.NDIS?.upload);

  const msg = {
    templateId: "d-6a182726d8cd46f49e45821c1d58bb7d",
    dynamicTemplateData: {
      subject: "New Participant Referral - " + formData.participant?.name,
      isParticipant: formData.isParticipant,
      consent: formData.consent,
      servicesRequired: formData.servicesRequired?.join(", "),
      participantName: formData.participant?.name,
      participantNamePreffered: formData.participant?.preferredName,
      dob: formData.participant?.dob,
      gender: formData.participant?.gender,
      pronouns: formData.participant?.pronouns,
      referrerName: formData.referrer?.name,
      referrerPhone: formData.referrer?.phone,
      referrerEmail: formData.referrer?.email,
      reason: formData.reason,
      primaryDisability: formData.primaryDisability,
      ndisNumber: formData.NDIS?.number,
      ndisFunded: formData.NDIS?.funded,
      ndisStartDate: formData.NDIS?.startDate,
      ndisEndDate: formData.NDIS?.endDate,
    },
    attachments: [
      {
        content: attachment,
        filename: "attachment" + getFileExtension(formData.NDIS?.upload?.type),
        type: formData.NDIS?.upload?.type,
        disposition: "attachment",
      },
    ],
  };

  return msg;
}

function blobToBase64(blob?: Blob): Promise<string | undefined> {
  if (!blob) return Promise.resolve("");
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64data = (reader.result as string).split(",")[1];
      resolve(base64data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function getFileExtension(mimeType?: string): string {
  switch (mimeType) {
    case "application/pdf":
      return ".pdf";
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return ".docx";
    case "image/png":
      return ".png";
    case "image/jpeg":
      return ".jpeg";
    default:
      return "";
  }
}
